import { use, useEffect, useRef, type ComponentType, type ReactNode } from 'react'
import { XIcon } from 'lucide-react'
import { useLingui } from '@lingui/react/macro'
import { cn } from '@/lib/utils'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import {
  DIALOG_OVERLAY_CLASS,
  DIALOG_POPUP_CLASS,
  DIALOG_FRAME_CLASS,
  DIALOG_VIEWPORT_CLASS,
  DialogEnterContext,
  SKELETON_EXIT_HOLD,
  markModalViewport,
  useRecordEnter,
} from '@/components/ui/dialog-surface'
import { useModalSlotPending } from '@/components/custom/modal-slot'
import {
  HORIZONTAL_CONTROL_CLASS,
  HORIZONTAL_LABEL_CLASS,
  HORIZONTAL_ROW_CLASS,
} from '@/components/custom/form/horizontal-field'

/**
 * The building blocks of a slot route's pending skeleton: the dialog's own surface, drawn with the dialog's class
 * lists, so the real dialog lands in the same box (docs/notes/app/loading-states.md). A skeleton paints only what is
 * not known yet; titles, labels and legends are static and paint as text.
 *
 * Everything here is eager (a pendingComponent is never code-split), so nothing here may import a form, base-ui's
 * dialog or anything else that would drag a dialog's weight into the entry.
 */
export function DialogSkeleton(props: DialogSkeletonProps) {
  const { open, close } = useModalSlotPending()
  return <DialogSkeletonSurface {...props} open={open} onClose={close} />
}

interface DialogSkeletonProps {
  /** The real `DialogContent`'s className: its width, height and padding. */
  className?: string
  /** The real `DialogContent`'s `frameClassName`. */
  frameClassName?: string
  /** The dialog's accessible name while it loads: its title, `SKELETON_VALUE` and all. */
  label: string
  showCloseButton?: boolean
  children: ReactNode
}

/**
 * The surface itself, for a skeleton outside the modal slot: a lazy overlay's (custom/lazy-overlay.tsx), which
 * passes its own open state and close.
 */
export function DialogSkeletonSurface(props: DialogSkeletonProps & { open: boolean; onClose: () => void }) {
  const { open, onClose: close } = props
  const popupRef = useRef<HTMLDivElement>(null)
  useRecordEnter(popupRef, use(DialogEnterContext), open)
  useEscapeToClose(open, close)

  // The surface's own `data-open`/`data-closed` put its enter and its exit on: closed before the dialog's code has
  // arrived, the skeleton stays up for the exit (custom/modal-slot.tsx), and the dialog carries on its enter
  // (ui/dialog-surface.ts).
  const state = open ? { 'data-open': '' } : { 'data-closed': '' }
  return (
    <>
      <div
        {...state}
        data-slot="dialog-skeleton-overlay"
        className={cn(DIALOG_OVERLAY_CLASS, SKELETON_EXIT_HOLD)}
        onClick={close}
      />
      <div {...state} ref={markModalViewport} data-slot="dialog-skeleton-viewport" className={DIALOG_VIEWPORT_CLASS}>
        <div
          className={cn(DIALOG_FRAME_CLASS, props.frameClassName)}
          onClick={(event) => {
            if (event.target === event.currentTarget) close()
          }}
        >
          <div
            ref={popupRef}
            {...state}
            data-slot="dialog-skeleton"
            role="dialog"
            aria-modal="true"
            aria-busy="true"
            aria-label={props.label.replace(SKELETON_VALUE, '…')}
            className={cn(DIALOG_POPUP_CLASS, SKELETON_EXIT_HOLD, props.className)}
          >
            {props.children}
            {props.showCloseButton !== false && (
              <Button variant="ghost" size="icon-sm" className="absolute top-2 right-2 z-20" onClick={close}>
                <XIcon />
                <span className="sr-only">Close</span>
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

function useEscapeToClose(open: boolean, close: () => void) {
  useEffect(() => {
    if (!open) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open, close])
}

/** `DialogHeader` + `DialogTitle`, with the title's text when it is static. */
export function DialogTitleSkeleton(props: { children: ReactNode; description?: ReactNode; className?: string }) {
  return (
    <div data-slot="dialog-header" className={cn('flex flex-col gap-2', props.className)}>
      <h2 data-slot="dialog-skeleton-title" className="text-base leading-none font-medium">
        {props.children}
      </h2>
      {props.description && <p className="text-muted-foreground text-sm">{props.description}</p>}
    </div>
  )
}

/**
 * Text that is known but not interactive yet (a button's label), or a stand-in whose length is the likely value:
 * drawn as a skeleton exactly the size the text will take, wrapping as it will.
 */
export function TextSkeleton(props: { children: ReactNode; className?: string }) {
  return (
    <span
      data-slot="skeleton"
      className={cn(
        'bg-muted animate-pulse rounded-md box-decoration-clone text-transparent select-none',
        props.className,
      )}
    >
      {props.children}
    </span>
  )
}

/**
 * A `<Button>`'s box around its label: `size` default, as the forms' submit buttons are, its label centred as a
 * button's is when a phone's footer stretches it. `iconStart` reserves a leading `mr-2 size-4` icon, `iconEnd` the
 * trailing icon (a chevron) the button draws after its label.
 */
export function ButtonSkeleton(props: { label: string; iconStart?: boolean; iconEnd?: boolean; className?: string }) {
  return (
    <Skeleton
      className={cn(
        'inline-flex h-8 shrink-0 items-center justify-center gap-1.5 rounded-lg border border-transparent px-2.5 text-sm font-medium whitespace-nowrap text-transparent select-none',
        props.className,
      )}
    >
      {props.iconStart && <span className="mr-2 size-4 shrink-0" />}
      {props.label}
      {props.iconEnd && <span className="size-4" />}
    </Skeleton>
  )
}

/** The value a title interpolates, as a translated message splits around it: `Edit job: ▢`. */
export const SKELETON_VALUE = '\u0000'

/** Renders a message built with `SKELETON_VALUE` in place of a value that is not known yet. */
export function MessageWithSkeleton(props: { message: string; chars?: number }) {
  // `align-top` keeps a `leading-none` title's line box at the text's own height.
  const [before, after] = props.message.split(SKELETON_VALUE)
  if (after === undefined) return before
  return (
    <>
      {before}
      <Skeleton className="inline-block h-[1em] align-top" style={{ width: `${props.chars ?? 8}ch` }} />
      {after}
    </>
  )
}

/** A form's box inside the dialog, as the horizontal forms draw it. */
export function FormSkeleton(props: { children: ReactNode }) {
  return <div className="flex min-w-0 flex-col">{props.children}</div>
}

/** One `<fieldset>` of a form: its legend (icon and text), then its rows. */
export function FieldsetSkeleton(props: {
  icon: ComponentType<{ className?: string; strokeWidth?: number }>
  legend: string
  /** `pb-10` where the form spaces a fieldset further, `pb-6` otherwise. */
  className?: string
  children: ReactNode
}) {
  const Icon = props.icon
  return (
    <fieldset className={cn('flex min-w-0 flex-col gap-2', props.className ?? 'pb-6')}>
      <legend className="text-muted-foreground mb-3 inline-flex items-center gap-1.5 text-xs font-medium">
        <Icon className="size-3.5" strokeWidth={2} />
        {props.legend}
      </legend>
      {props.children}
    </fieldset>
  )
}

/**
 * A horizontal field row (`orientation="horizontal"`), in the fields' own layout (form/horizontal-field.ts): the label
 * above its control on a phone and in its `w-48` column from `sm` up, and the control's box as a skeleton. `control`
 * picks the box: an `h-8` input (text, select, combobox, date), a switch, or the photo upload, which stays beside its
 * label at every width, as the upload field draws it.
 */
export function FieldRowSkeleton(props: { label: string; required?: boolean; control?: 'input' | 'switch' | 'photo' }) {
  const control = props.control ?? 'input'
  const label = (
    <div
      className={cn(
        'flex items-center justify-between gap-6',
        control === 'photo' ? 'h-8 w-48 shrink-0' : HORIZONTAL_LABEL_CLASS,
      )}
    >
      <div className="flex h-5 items-center gap-1">
        <span className="flex items-center gap-2 text-sm leading-none font-medium select-none">
          {props.label}
          {props.required && '*'}
        </span>
      </div>
    </div>
  )
  if (control === 'switch') {
    return (
      <div className="flex items-center gap-3">
        {label}
        <Skeleton className="h-[18.4px] w-8 rounded-full" />
      </div>
    )
  }
  if (control === 'photo') {
    return (
      <div className="flex min-w-0 flex-1 items-start gap-3">
        {label}
        <div className="min-w-0 flex-1">
          <DropZoneSkeleton />
        </div>
      </div>
    )
  }
  return (
    <div className={HORIZONTAL_ROW_CLASS}>
      {label}
      <div className={HORIZONTAL_CONTROL_CLASS}>
        <Skeleton className="h-8 w-full rounded-lg" />
      </div>
    </div>
  )
}

/**
 * The photo upload's drop zone (form/-components/drop-zone.tsx) as one skeleton box: its icon, its wrapping text and
 * its button are laid out invisibly inside, so the box is as tall as the drop zone at any width. A fixed height
 * matched only the desktop column; on a phone the text wraps onto more lines.
 */
function DropZoneSkeleton() {
  const { t } = useLingui()
  return (
    <Skeleton className="flex flex-col items-center justify-center rounded-xl border border-transparent px-6 py-10">
      {/* Each its own flex item, as in the drop zone: a wrapper around them took the button's width for the text. */}
      <div className="invisible size-8" />
      <p className="invisible mt-3 text-center text-sm">{t`Drag and drop the file here, or upload from your computer`}</p>
      <div className="invisible mt-3 flex h-9 items-center border border-transparent px-2.5 text-sm font-medium whitespace-nowrap">
        {t`Upload photo`}
      </div>
    </Skeleton>
  )
}

/** The row under a form's fieldsets that holds its submit button. */
export function SubmitRowSkeleton(props: { label: string }) {
  return (
    <div className="flex min-w-0 gap-4">
      <ButtonSkeleton label={props.label} />
    </div>
  )
}
